import React, { useEffect, useId, useMemo, useRef, useState } from "react";

import CircularCheckbox from "../form/CircularCheckbox";
import { FilterAltIcon } from "../icons/FilterAltIcon";

function countSelectedValues(selectedValuesBySection) {
  return Object.values(selectedValuesBySection || {}).reduce(
    (count, values) => count + (Array.isArray(values) ? values.length : 0),
    0,
  );
}

function getEffectiveSelectedValues(section, selectedValuesBySection) {
  const selectedValues = Array.isArray(selectedValuesBySection?.[section?.key])
    ? selectedValuesBySection[section.key]
    : [];

  if (
    section?.defaultSelectionMode === "all" &&
    selectedValues.length === 0 &&
    Array.isArray(section?.options)
  ) {
    return section.options.map((option) => option.value);
  }

  return selectedValues;
}

function slugifyToken(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function FilterMenuButton({
  buttonClassName = "small-outlined-button !min-h-0 !px-3 !py-1.5",
  buttonLabel = "Filter",
  clearLabel = "Clear",
  emptyLabel = "No filters available.",
  sections = [],
  selectedValuesBySection = {},
  onToggleValue,
  onClearAll,
  className = "",
  iconOnly = false,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const baseId = useId();

  const activeCount = useMemo(
    () => countSelectedValues(selectedValuesBySection),
    [selectedValuesBySection],
  );
  const hasActiveFilters = activeCount > 0;

  const visibleSections = useMemo(
    () =>
      sections.filter(
        (section) =>
          Array.isArray(section?.options) && section.options.length > 0,
      ),
    [sections],
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDownOutside = (event) => {
      if (containerRef.current?.contains(event.target)) {
        return;
      }

      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("touchstart", handlePointerDownOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={`relative ${className}`.trim()}>
      <button
        type="button"
        className={`${buttonClassName} !relative !flex !items-center ${
          iconOnly ? "!justify-center !px-2 !py-2" : "!gap-2 whitespace-nowrap"
        } ${hasActiveFilters ? "!border-primary !bg-primary !text-greyscale-1" : ""}`}
        aria-label={buttonLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={buttonLabel}
        onClick={() => setOpen((current) => !current)}
      >
        <FilterAltIcon className="h-4 w-4 fill-current" aria-hidden="true" />
        {iconOnly ? (
          <span className="sr-only">{buttonLabel}</span>
        ) : (
          <span>{buttonLabel}</span>
        )}
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-20 mt-2 w-80 max-w-[calc(100vw-2rem)]">
          <div className="popover !w-auto !p-0 overflow-hidden">
            <div className="flex items-center justify-between border-b border-greyscale-1 px-4 py-3">
              <h4 className="text-sm font-semibold text-typography">
                {buttonLabel}
              </h4>

              {activeCount > 0 && onClearAll ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-primary transition-colors hover:text-primary/80"
                  onClick={onClearAll}
                >
                  {clearLabel}
                </button>
              ) : null}
            </div>

            {visibleSections.length ? (
              <div className="max-h-[24rem] overflow-auto px-4 py-3">
                {visibleSections.map((section, index) => (
                  <fieldset
                    key={section.key}
                    className={`flex flex-col gap-2 ${
                      index > 0
                        ? "mt-3 border-t border-greyscale-1 pt-3"
                        : undefined
                    }`}
                  >
                    <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-greyscale-3">
                      {section.title}
                    </legend>

                    {section.variant === "slider" ? (
                      <div
                        role="group"
                        aria-label={section.title}
                        className="flex items-center gap-2 rounded-full bg-greyscale-1 p-1"
                      >
                        {section.options.map((option) => {
                          const selectedValues = getEffectiveSelectedValues(
                            section,
                            selectedValuesBySection,
                          );
                          const selected = selectedValues.includes(option.value);

                          return (
                            <button
                              key={option.value}
                              type="button"
                              aria-label={option.label}
                              aria-pressed={selected}
                              title={option.label}
                              className={`h-9 min-w-0 flex-1 rounded-full border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                                option.swatchClassName || "bg-greyscale-1"
                              } ${
                                selected
                                  ? "border-primary opacity-100 shadow-sm"
                                  : "border-transparent opacity-60 hover:opacity-90"
                              }`}
                              onClick={() =>
                                onToggleValue?.(
                                  section.key,
                                  option.value,
                                  section.selectionMode,
                                  section,
                                )
                              }
                            >
                              <span className="sr-only">{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      section.options.map((option) => {
                        const inputId = `${baseId}-${section.key}-${slugifyToken(
                          option.value,
                        )}`;
                        const selectedValues = getEffectiveSelectedValues(
                          section,
                          selectedValuesBySection,
                        );
                        const checked = selectedValues.includes(option.value);

                        return (
                          <label
                            key={option.value}
                            htmlFor={inputId}
                            className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-greyscale-0.5"
                          >
                            <CircularCheckbox
                              id={inputId}
                              checked={checked}
                              onChange={() =>
                                onToggleValue?.(
                                  section.key,
                                  option.value,
                                  section.selectionMode,
                                  section,
                                )
                              }
                            />

                            <span className="min-w-0 flex-1 text-sm text-typography">
                              {option.label}
                            </span>

                            {typeof option.count === "number" ? (
                              <span className="text-xs text-greyscale-3">
                                {option.count}
                              </span>
                            ) : null}
                          </label>
                        );
                      })
                    )}
                  </fieldset>
                ))}
              </div>
            ) : (
              <p className="px-4 py-3 text-sm text-greyscale-3">{emptyLabel}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
